import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertProps, Slide, SlideProps } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { logger } from '../utils/logger';

export type ErrorSeverity = 'error' | 'warning' | 'info' | 'success';

export interface ErrorNotification {
  id: string;
  message: string;
  severity: ErrorSeverity;
  details?: string;
  statusCode?: number;
  timestamp: Date;
  dismissible?: boolean;
  autoHideDuration?: number;
  action?: {
    label: string;
    handler: () => void;
  };
}

interface ErrorContextType {
  errors: ErrorNotification[];
  showError: (
    message: string,
    severity?: ErrorSeverity,
    options?: Partial<ErrorNotification>
  ) => void;
  dismissError: (id: string) => void;
  clearAllErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

// Slide transition component
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
  maxErrors?: number;
  defaultAutoHideDuration?: number;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({
  children,
  maxErrors = 3,
  defaultAutoHideDuration = 6000
}) => {
  const [errors, setErrors] = useState<ErrorNotification[]>([]);
  const [currentError, setCurrentError] = useState<ErrorNotification | null>(null);

  const showError = useCallback(
    (
      message: string,
      severity: ErrorSeverity = 'error',
      options: Partial<ErrorNotification> = {}
    ) => {
      const newError: ErrorNotification = {
        id: `error-${Date.now()}-${Math.random()}`,
        message,
        severity,
        timestamp: new Date(),
        dismissible: true,
        autoHideDuration: defaultAutoHideDuration,
        ...options
      };

      // エラーログ記録
      logger.error(`Error notification shown: ${message}`, {
        context: 'ErrorContext',
        severity,
        statusCode: options.statusCode,
        details: options.details
      });

      setErrors((prev) => {
        const updated = [...prev, newError];
        // 最大件数を超えたら古いものから削除
        if (updated.length > maxErrors) {
          return updated.slice(-maxErrors);
        }
        return updated;
      });

      // 最新のエラーを表示
      setCurrentError(newError);
    },
    [defaultAutoHideDuration, maxErrors]
  );

  const dismissError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((error) => error.id !== id));
    setCurrentError((current) => (current?.id === id ? null : current));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
    setCurrentError(null);
  }, []);

  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      if (currentError) {
        dismissError(currentError.id);
      }
    },
    [currentError, dismissError]
  );

  // エラーの重要度に応じたアラートプロパティを取得
  const getAlertProps = (severity: ErrorSeverity): Partial<AlertProps> => {
    switch (severity) {
      case 'error':
        return {
          severity: 'error',
          variant: 'filled'
        };
      case 'warning':
        return {
          severity: 'warning',
          variant: 'filled'
        };
      case 'info':
        return {
          severity: 'info',
          variant: 'standard'
        };
      case 'success':
        return {
          severity: 'success',
          variant: 'standard'
        };
      default:
        return {
          severity: 'error',
          variant: 'filled'
        };
    }
  };

  const value: ErrorContextType = {
    errors,
    showError,
    dismissError,
    clearAllErrors
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      {currentError && (
        <Snackbar
          open={!!currentError}
          autoHideDuration={currentError.autoHideDuration}
          onClose={handleClose}
          TransitionComponent={SlideTransition as React.ComponentType<TransitionProps>}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={currentError.dismissible ? handleClose : undefined}
            {...getAlertProps(currentError.severity)}
            sx={{
              width: '100%',
              minWidth: '300px',
              maxWidth: '600px',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            action={
              currentError.action ? (
                <button
                  onClick={currentError.action.handler}
                  style={{
                    background: 'none',
                    border: '1px solid currentColor',
                    color: 'inherit',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginLeft: '8px'
                  }}
                >
                  {currentError.action.label}
                </button>
              ) : undefined
            }
          >
            <div>
              <div>{currentError.message}</div>
              {currentError.details && (
                <div style={{ fontSize: '0.875rem', marginTop: '4px', opacity: 0.9 }}>
                  {currentError.details}
                </div>
              )}
              {process.env.NODE_ENV === 'development' && currentError.statusCode && (
                <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.7 }}>
                  Status Code: {currentError.statusCode}
                </div>
              )}
            </div>
          </Alert>
        </Snackbar>
      )}
    </ErrorContext.Provider>
  );
};
